import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";
import {
  hasDisapprovalReason,
  resolveReasonLabel,
} from "@/pages/(app)/returns/view/components/disapprove-return/resolve-reason-label";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { LightningBoltIcon } from "@radix-ui/react-icons";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";

type Props = { activity: ReturnRequestLogDto };

type DisapprovalParams = {
  reasonId?: string | null;
  reasonLabel?: string | null;
  message?: string | null;
};

export default function DisapprovedActivity({ activity }: Props) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const params = (activity.params ?? {}) as DisapprovalParams;
  const author = shop.users.find(
    (user) => user.userId === activity.author,
  )?.user;
  const authorName = getAuthorName(author);

  const header = (
    <ActivityItemHeader title="" timestamp={activity.date}>
      <span>{t("returnDetail.disapproved.title")}</span>
      {authorName && (
        <>
          <span className="font-normal">{t("returnDetail.by")}</span>
          <span className="font-semibold">{authorName}</span>
        </>
      )}
    </ActivityItemHeader>
  );

  // A reason recorded without a wording still counts: gating on the message
  // alone would hide which reason was picked. With neither — the shop does not
  // ask for one — there is nothing to unfold, so the entry stays flat instead of
  // offering an empty drawer.
  const isUnfoldable = hasDisapprovalReason(params);

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <LightningBoltIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        {isUnfoldable ? (
          <div className="flex items-center gap-2 flex-1 justify-between w-full min-w-0">
            <Accordion
              type="single"
              collapsible
              className="flex-1 min-w-0 w-full"
            >
              <AccordionItem value="disapproval" className="border-0">
                <AccordionTrigger className="p-0 w-full min-w-0">
                  {header}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mt-1 flex flex-col gap-1">
                    <p className="text-xs font-medium">
                      {resolveReasonLabel(params, shop.disapprovalReasons, t)}
                    </p>
                    {params.message && (
                      <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                        {params.message}
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ) : (
          header
        )}
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
